/**
 * Declarative question schemas for the three T-TITE calculators.
 *
 * The wizard, the review-and-edit summary and all validation are driven entirely
 * from this file, so a question only ever has to be described once.
 *
 * Pricing logic lives on the server (backend/pricing_engine.py) and is never
 * duplicated here — eligibility is checked by calling /api/session/{id}/evaluate,
 * which returns a fallback decision WITHOUT ever revealing a price.
 */

import { getPath, isAnswered, isYes } from "@/lib/paths";
import { formatCount, money } from "@/lib/format";

export const SERVICES = [
  {
    key: "afs",
    title: "Preparation of Financial Statements",
    tagline: "Get professionally prepared annual financial statements for your business.",
    detail:
      "We build your trial balance from your bank statements and prepare compliant annual financial statements. Up to two financial years.",
    icon: "FileSpreadsheet",
    priceHint: "Once-off fee",
    testId: "service-chooser-afs-card",
  },
  {
    key: "assurance",
    title: "Audit or Independent Review",
    tagline: "Get pricing for your audit or independent review.",
    detail:
      "For companies, NPCs and NPOs requiring independent assurance over their financial statements. Select an audit or independent review to get started.",
    icon: "ShieldCheck",
    priceHint: "Once-off fee",
    testId: "service-chooser-audit-card",
  },
  {
    key: "bookkeeping",
    title: "Bookkeeping Services",
    tagline: "Ongoing monthly transaction processing and reconciliation.",
    detail:
      "Monthly processing, reconciliations and reliable accounting support. Online estimates are currently available for businesses with annual turnover up to R20 million.",
    icon: "Calculator",
    priceHint: "Monthly fee",
    testId: "service-chooser-bookkeeping-card",
  },
];

export const SERVICE_BY_KEY = SERVICES.reduce((acc, s) => ({ ...acc, [s.key]: s }), {});

export const DEFAULT_ANSWERS = {
  afs: {},
  assurance: {},
  // Q8 defaults to the new-setup option per the brief.
  bookkeeping: { books_origin: "new", accounting_system: "own_keep" },
};

const YEAR_CHIPS = ["Most recent financial year", "The financial year before that"];

export function requestedYears(answers) {
  const raw = getPath(answers, "years");
  return String(raw) === "2" ? 2 : 1;
}

/* ------------------------------------------------------------------ */
/* Shared question fragments                                           */
/* ------------------------------------------------------------------ */

const YEARS_FIELD = (verb) => ({
  type: "choice",
  path: "years",
  label: `How many years ${verb}`,
  help: "This calculator covers up to two financial years. A longer backlog is best handled by a quick chat with our team.",
  required: true,
  reviewLabel: "How many years",
  options: [
    { value: "1", label: "Just this year", hint: "One financial year." },
    {
      value: "2",
      label: "This year and last year (2 years)",
      hint: "We'll ask for each year's figures separately.",
    },
    {
      value: "more",
      label: "More than 2 years",
      hint: "A longer backlog needs a proper look before we can quote.",
      routesToFallback: true,
    },
  ],
});

/* ------------------------------------------------------------------ */
/* 1. Preparation of Financial Statements                              */
/* ------------------------------------------------------------------ */

function afsYearStep(index, years) {
  const p = `year_data.${index}`;
  return {
    key: `afs-year-${index}`,
    title: years > 1 ? YEAR_CHIPS[index] : "Your financial year",
    subtitle:
      years > 1
        ? `Figures for financial year ${index + 1} of ${years}. Each year is priced separately.`
        : "Tell us about the year we'll be preparing statements for.",
    chip: years > 1 ? `Year ${index + 1} of ${years}` : null,
    fields: [
      {
        type: "yesno",
        path: `${p}.dormant`,
        label: "Was this a dormant or pre-trading year?",
        help: "Dormant means the company had no transactions at all during the year.",
        tooltip:
          "A dormant company traded nothing for the year — no sales, no expenses, no money moving through the bank.",
        required: true,
        yesHint: "No transactions at all",
        noHint: "The business was trading",
        reviewLabel: "Dormant / pre-trading this year",
      },
      {
        type: "currency",
        path: `${p}.turnover`,
        label: "Annual turnover for this year",
        emphasis: true,
        help: "This figure determines your price — please enter it as accurately as you can. It's the total sales or income for the year, before any expenses.",
        tooltip:
          "Turnover is your total sales or income for the financial year, before deducting any costs.",
        required: true,
        min: 0,
        reviewLabel: "Annual turnover",
      },
      {
        type: "number",
        path: `${p}.transactions_per_month`,
        label: "Average bank transactions per month, across all accounts",
        emphasis: true,
        help: "This drives the transaction volume portion of your price directly — please enter it as accurately as you can. Count every line on your bank statements, money in and money out.",
        tooltip:
          "Add up the lines on all your business bank statements for a typical month — both payments received and payments made.",
        required: true,
        min: 0,
        reviewLabel: "Average bank transactions a month",
        showIf: (answers) => !isYes(getPath(answers, `${p}.dormant`)),
      },
    ],
  };
}

function afsSteps(answers) {
  const years = requestedYears(answers);
  const steps = [
    {
      key: "afs-years",
      title: "How many years do you need?",
      subtitle:
        "Our pricing assumes you'll give us bank statements only — building the trial balance from scratch is included in the fee.",
      fields: [YEARS_FIELD("of financial statements do you need?")],
    },
    afsYearStep(0, years),
  ];

  if (years === 2) steps.push(afsYearStep(1, years));

  steps.push({
    key: "afs-complexity",
    title: "A bit about your business",
    subtitle:
      "These describe the business itself rather than any one year, so you only answer them once — even if we're quoting two years.",
    fields: [
      {
        type: "yesno",
        path: "complexity.first_year_trading",
        label: "Is this the company's first year of trading?",
        help: "A brand-new business starting from nothing — there are no earlier records to pick up.",
        tooltip:
          "Answer Yes only if the business genuinely started trading in this financial year. If it has been going for a while, answer No.",
        required: true,
        yesHint: "Brand new business",
        noHint: "It's been trading before",
        reviewLabel: "The company's first year of trading",
      },
      {
        type: "yesno",
        path: "complexity.first_year_with_ttite",
        label: "Is this the first year you'll be working with T-TITE?",
        help: "Taking over an established set of records from someone else takes extra work up front. It's charged once, not per year.",
        tooltip:
          "We need to pick up where your previous accountant left off, check the opening balances and satisfy ourselves that they're right.",
        required: true,
        yesHint: "First time with us",
        noHint: "We already work together",
        reviewLabel: "First year working with T-TITE",
        showIf: (answers) => !isYes(getPath(answers, "complexity.first_year_trading")),
      },
      {
        type: "yesno",
        path: "complexity.inventory",
        label: "Does the company hold inventory or stock?",
        tooltip:
          "Inventory is goods you buy or make and hold to sell later — shop stock, raw materials, finished products.",
        required: true,
        reviewLabel: "Holds inventory / stock",
      },
      {
        type: "yesno",
        path: "complexity.ppe_over_10",
        label:
          "Will your fixed asset register have more than 10 items (vehicles, equipment, computers, furniture)?",
        tooltip:
          "Also called the Property, Plant & Equipment register — the list of bigger items the business owns and uses over several years.",
        required: true,
        reviewLabel: "Fixed asset register has more than 10 items",
      },
      {
        type: "yesno",
        path: "complexity.banks_3plus",
        label: "Does the company have 3 or more bank accounts?",
        required: true,
        reviewLabel: "Three or more bank accounts",
      },
      {
        type: "yesno",
        path: "complexity.income_3plus",
        label: "Does the company have 3 or more distinct income streams?",
        tooltip:
          "Separate ways the business earns money — for example product sales, rental income and consulting fees.",
        required: true,
        reviewLabel: "Three or more income streams",
      },
      {
        type: "yesno",
        path: "complexity.director_loans",
        label: "Are there multiple directors or members with loan accounts to reconcile?",
        tooltip:
          "A director's loan account tracks money moving between the business and its owners personally.",
        required: true,
        reviewLabel: "Multiple directors / members with loan accounts",
      },
    ],
  });

  const addonYearsField = (key, label) => ({
    type: "choice",
    path: `addons.${key}_years`,
    label,
    help: "Each year is a separate submission, so you can take just the most recent one if you prefer.",
    required: true,
    options: [
      {
        value: "1",
        label: "Most recent financial year only",
        hint: "Just the latest year.",
      },
      {
        value: "2",
        label: "Both financial years",
        hint: "Both of the years we're quoting.",
      },
    ],
  });

  steps.push({
    key: "afs-addons",
    title: "Anything else while we're at it?",
    subtitle:
      "Both are optional, and both are cheaper when we do them alongside your financial statements. Each appears as its own line on your quote.",
    fields: [
      {
        type: "yesno",
        path: "addons.income_tax_return",
        label: "Would you also like us to prepare your Income Tax Return?",
        help: "R950 per financial year on its own — R650 per year when bundled here. You save R300 a year.",
        yesHint: "Yes — save R300 a year",
        noHint: "Not this time",
        required: true,
        reviewLabel: "Income Tax Return preparation",
      },
      {
        ...addonYearsField("income_tax_return", "Which years should we do the Income Tax Return for?"),
        reviewLabel: "Income Tax Return — which years",
        showIf: (answers) =>
          requestedYears(answers) === 2 && isYes(getPath(answers, "addons.income_tax_return")),
      },
      {
        type: "yesno",
        path: "addons.cipc_annual_return",
        label: "Would you also like us to handle your CIPC Annual Return?",
        help: "R450 per financial year on its own — R250 per year when bundled here. You save R200 a year.",
        tooltip:
          "Every registered company has to file an annual return with the CIPC to stay in good standing.",
        yesHint: "Yes — save R200 a year",
        noHint: "Not this time",
        required: true,
        reviewLabel: "CIPC Annual Return",
      },
      {
        ...addonYearsField("cipc_annual_return", "Which years should we do the CIPC Annual Return for?"),
        reviewLabel: "CIPC Annual Return — which years",
        showIf: (answers) =>
          requestedYears(answers) === 2 && isYes(getPath(answers, "addons.cipc_annual_return")),
      },
    ],
  });

  return steps;
}

/* ------------------------------------------------------------------ */
/* 2. Audit & Independent Review                                       */
/* ------------------------------------------------------------------ */

function assuranceYearStep(index, years) {
  const p = `year_data.${index}`;
  return {
    key: `assurance-year-${index}`,
    title: years > 1 ? YEAR_CHIPS[index] : "Your financial year",
    subtitle:
      years > 1
        ? `Figures for financial year ${index + 1} of ${years}. Each year is priced separately.`
        : "Your year-end figures. Estimates are fine if you don't have the exact numbers to hand.",
    chip: years > 1 ? `Year ${index + 1} of ${years}` : null,
    fields: [
      {
        type: "currency",
        path: `${p}.turnover`,
        label: "Annual turnover",
        emphasis: true,
        help: "This figure helps determine your price — please enter it as accurately as you can.",
        tooltip: "Your total sales or income for the financial year, before deducting any costs.",
        required: true,
        min: 0,
        reviewLabel: "Annual turnover",
      },
      {
        type: "currency",
        path: `${p}.total_assets`,
        label: "Total assets at year-end",
        help: "Everything the business owns, added together.",
        tooltip:
          "Total assets is everything the business owns — cash in the bank, money owed to you, stock, vehicles, equipment and property.",
        required: true,
        min: 0,
        reviewLabel: "Total assets at year-end",
      },
      {
        type: "currency",
        path: `${p}.total_liabilities`,
        label: "Total liabilities at year-end",
        help: "Everything the business owes, added together.",
        tooltip:
          "Total liabilities is everything the business owes — loans, credit cards, money owed to suppliers, and tax due to SARS.",
        required: true,
        min: 0,
        reviewLabel: "Total liabilities at year-end",
      },
      {
        type: "currency",
        path: `${p}.trade_receivables`,
        label: "Trade receivables at year-end",
        help: "Money customers still owed you at year-end.",
        tooltip:
          "Trade receivables (also called debtors) is money your customers still owed you at year-end for work already done or goods already delivered.",
        required: true,
        min: 0,
        reviewLabel: "Trade receivables at year-end",
      },
      {
        type: "currency",
        path: `${p}.net_profit_before_tax`,
        label: "Net profit or loss before tax",
        help: "If the business made a loss, enter it with a minus sign in front (for example -150000).",
        tooltip:
          "What was left over after all costs but before income tax. A loss is entered as a negative number.",
        required: true,
        allowNegative: true,
        reviewLabel: "Net profit / (loss) before tax",
      },
    ],
  };
}

function assuranceSteps(answers) {
  const years = requestedYears(answers);
  const mode = getPath(answers, "service_mode");
  const modeLabel = mode === "review" ? "independent review" : "audit";

  const steps = [
    {
      key: "assurance-entity",
      title: "What type of entity needs to be audited or reviewed?",
      subtitle: "This tells us which set of rules and pricing applies.",
      fields: [
        {
          type: "choice",
          path: "entity_type",
          label: "What type of entity needs to be audited or reviewed?",
          required: true,
          reviewLabel: "Type of entity",
          options: [
            {
              value: "pty",
              label: "Private Company (Pty) Ltd",
              hint: "A company registered with the CIPC.",
            },
            {
              value: "npo",
              label: "NPC / NPO",
              hint: "A non-profit company or registered non-profit organisation.",
            },
            {
              value: "trust",
              label: "Trust",
              hint: "Trust audits are a separate service with their own scope, so we quote those directly.",
              routesToFallback: true,
            },
          ],
        },
      ],
    },
    {
      key: "assurance-service",
      title: "Which service do you need?",
      subtitle:
        "You'll usually know which one applies from your lenders, your regulator or your founding documents. Not sure you need either? Our Preparation of Financial Statements service may be all you need.",
      fields: [
        {
          type: "choice",
          path: "service_mode",
          label: "Which service do you need?",
          required: true,
          reviewLabel: "Service required",
          options: [
            {
              value: "audit",
              label: "Audit",
              hint: "The highest level of assurance. Often required by lenders, regulators, or your organisation's own founding documents.",
            },
            {
              value: "review",
              label: "Independent Review",
              hint: "A lower, more limited level of assurance. Often the right fit for smaller organisations that don't require a full audit.",
            },
          ],
        },
      ],
    },
    {
      key: "assurance-years",
      title: "How many years do you need?",
      subtitle: "Each year is priced separately, then added together.",
      fields: [YEARS_FIELD(`do you need ${modeLabel === "audit" ? "audited" : "reviewed"}?`)],
    },
    assuranceYearStep(0, years),
  ];

  if (years === 2) steps.push(assuranceYearStep(1, years));

  steps.push({
    key: "assurance-complexity",
    title: "A bit about your business",
    subtitle:
      "These describe the business itself rather than any one year, so you only answer them once.",
    fields: [
      {
        type: "yesno",
        path: "complexity.first_year",
        label: `Is this the first year T-TITE will be ${
          mode === "review" ? "reviewing" : "auditing"
        } your business?`,
        required: true,
        reviewLabel: "First year T-TITE will be auditing / reviewing the business",
      },
      {
        type: "yesno",
        path: "complexity.group_structure",
        label: "Is there a group structure or multiple related entities?",
        tooltip:
          "For example a holding company with subsidiaries, or several companies under common ownership.",
        required: true,
        reviewLabel: "Group structure / multiple related entities",
      },
      {
        type: "yesno",
        path: "complexity.related_party",
        label: "Are there related party transactions that need specific testing?",
        tooltip:
          "Business done with people or companies connected to the owners — for example renting premises from a director.",
        required: true,
        reviewLabel: "Related party transactions requiring specific testing",
      },
      {
        type: "yesno",
        path: "complexity.banks_3plus",
        label: "Does the company have 3 or more bank accounts?",
        required: true,
        reviewLabel: "Three or more bank accounts",
      },
    ],
  });

  const checksStep = {
    key: "assurance-checks",
    title: "Before the figures, a few quick checks",
    subtitle:
      "We ask these up front so we don't take you through the figures for nothing. If something here means we can't give you a reliable automatic number, we'll tell you right away and put you straight through to our team.",
    fields: [
      {
        type: "yesno",
        path: "stop_checks.prior_modified_opinion",
        label:
          "Did the prior year have a modified opinion (qualified, adverse or disclaimer) or review conclusion?",
        tooltip:
          "A modified opinion means last year's auditor or reviewer couldn't sign off cleanly and had to add a qualification.",
        required: true,
        reviewLabel: "Prior year had a modified opinion or review conclusion",
      },
      {
        type: "yesno",
        path: "stop_checks.foreign_operations",
        label: "Are there foreign operations or subsidiaries involved?",
        required: true,
        reviewLabel: "Foreign operations or subsidiaries involved",
      },
      {
        type: "yesno",
        path: "stop_checks.has_prior_assurance",
        label: "Has this business been audited or independently reviewed before, by any firm?",
        help: "Not just by us — by anyone. A first-ever audit or review needs extra work on the opening figures, so we'd rather scope that with you directly.",
        tooltip:
          "We're asking whether any firm has ever signed off an audit report or a review conclusion for this business.",
        yesHint: "Yes — it's been done before",
        noHint: "No — this would be the first time",
        required: true,
        reviewLabel: "Has been audited or reviewed before, by any firm",
      },
      {
        // Only asked when nobody has ever audited or reviewed the business. A
        // brand-new business has no earlier figures to pick up, so a first audit
        // is ordinary work and we can still price it here. An older business
        // that has never been audited means a backlog, which needs a person.
        type: "yesno",
        path: "stop_checks.first_year_trading",
        label: "Is this the business's first year of trade or generating revenue?",
        help: "If the business has only just started trading, there are no earlier figures to pick up, so we can still give you a price online.",
        tooltip:
          "We're asking whether the year you want audited or reviewed is the first year the business traded or earned any income.",
        yesHint: "Yes — it started trading in this year",
        noHint: "No — it has traded in earlier years too",
        required: true,
        showIf: (answers) =>
          isAnswered(getPath(answers, "stop_checks.has_prior_assurance"))
          && !isYes(getPath(answers, "stop_checks.has_prior_assurance")),
        reviewLabel: "First year of trade or generating revenue",
      },
      {
        type: "yesno",
        path: "stop_checks.inventory_significant",
        label: "Does the company hold inventory that is significant in value?",
        tooltip:
          "Stock or goods held for sale where the value is big enough to matter to the overall picture of the business.",
        required: true,
        reviewLabel: "Holds inventory significant in value",
      },
      {
        type: "yesno",
        path: "stop_checks.multiple_locations",
        label: "Does the entity operate in more than one location?",
        tooltip:
          "More than one branch, site, depot or trading address - not just staff working remotely.",
        required: true,
        reviewLabel: "Operates in more than one location",
      },
      {
        type: "yesno",
        path: "stop_checks.regulated_sector",
        label:
          "Does the business operate in banking or financial services, healthcare, cryptocurrency / digital assets, or retail?",
        help: "These sectors carry extra regulatory requirements, so we scope them with you rather than quoting automatically.",
        required: true,
        reviewLabel: "Operates in a specialised or regulated sector",
      },
      {
        type: "yesno",
        path: "stop_checks.complex_systems",
        label:
          "Does the business use complex, custom-built, highly automated, or interconnected software systems?",
        tooltip:
          "For example bespoke software written for you, or several systems wired together so data flows between them automatically.",
        required: true,
        reviewLabel: "Uses complex or custom-built software systems",
      },
    ],
  };

  // The checks go in THIRD: entity type, then audit or independent review, then
  // the checks - so anything that rules out an automatic quote is still caught
  // before the customer is asked for a single figure. The flow re-prices after
  // every step, so they hear about it there and then rather than at the end.
  steps.splice(2, 0, checksStep);

  return steps;
}

/* ------------------------------------------------------------------ */
/* 3. Bookkeeping                                                      */
/* ------------------------------------------------------------------ */

/**
 * The customer picks a broad transaction range, then narrows it. Picking a new
 * broad range wipes the narrow one and the count it settled, so what we price
 * is always what is on screen.
 */
const CLEARS_TXN_RANGE = { txn_range: null, transactions_per_month: null };

/**
 * Above the volume our standard monthly pricing is built for. Not a refusal:
 * a bookkeeping job this size is worth scoping properly.
 */
const HIGH_VOLUME_FALLBACK = {
  headline: "Let's scope this one properly",
  body: "At this volume your bookkeeping needs a tailored scope rather than a standard monthly fee - the work depends on how the transactions arrive and how your accounts are structured. Leave your details and our team will come back to you to discuss your requirements.",
  reasons: [],
};

/**
 * The second, narrower question. One of these is shown per broad range.
 *
 * Each option carries the transaction count our pricing works in, so the fee
 * comes from the bands on the rate card and nothing here decides a price. If a
 * range sits above the highest band, the server says so and we ask for their
 * details instead - add a band for it on the rate card and it prices itself.
 */
function txnRangeField(band, options) {
  return {
    type: "choice",
    path: "txn_range",
    label: "Which range is closest to your typical month?",
    help: "We use this range to match your business to the correct indicative monthly bookkeeping fee.",
    required: true,
    requiredMessage: "Please choose the range closest to your typical month.",
    reviewLabel: "Transactions a month (closest range)",
    showIf: (answers) => getPath(answers, "txn_band") === band,
    options: options.map(([value, label, count]) => ({
      value,
      label,
      sets: { transactions_per_month: count },
    })),
  };
}


function bookkeepingSteps() {
  return [
    {
      key: "bookkeeping-turnover",
      title: "First, the size of your business",
      subtitle:
        "One quick question before the pricing questions, so we can point you at the right service.",
      fields: [
        {
          type: "choice",
          path: "revenue_r20m_or_less",
          label: "Is your business's annual revenue R20 million or less?",
          emphasis: true,
          help: "Our standard bookkeeping pricing is designed for businesses with annual revenue of R20 million or less.",
          tooltip: "Your total sales or income for a year, before deducting any costs.",
          required: true,
          reviewLabel: "Annual revenue R20 million or less",
          options: [
            {
              value: "yes",
              label: "Yes - R20 million or less",
              hint: "Our standard bookkeeping pricing is built for this.",
            },
            {
              value: "no",
              label: "No - more than R20 million",
              hint: "We'll show you the finance function option, and you can still carry on with bookkeeping.",
            },
          ],
        },
      ],
    },
    {
      key: "bookkeeping-volume",
      title: "How busy are your bank accounts?",
      subtitle: "This is the single biggest driver of your monthly fee.",
      fields: [
        {
          type: "choice",
          path: "txn_band",
          label:
            "About how many transactions go through all your business bank accounts in a typical month?",
          emphasis: true,
          help: "Choose the broad range that fits best. We'll narrow it down in the next step so we can calculate your indicative monthly fee accurately.",
          note: "Include transactions across all your business bank accounts. If volumes vary, choose the range that best represents a typical month.",
          tooltip:
            "Count the lines on all your business bank statements for a typical month — both payments received and payments made.",
          required: true,
          reviewLabel: "Transactions a month",
          /* Changing the broad range clears the narrow one underneath it, so a
             range from a previous answer is never the one we price. */
          options: [
            { value: "up_to_150", label: "Up to 150 transactions", sets: CLEARS_TXN_RANGE },
            { value: "151_300", label: "151–300 transactions", sets: CLEARS_TXN_RANGE },
            { value: "301_500", label: "301–500 transactions", sets: CLEARS_TXN_RANGE },
            { value: "501_800", label: "501–800 transactions", sets: CLEARS_TXN_RANGE },
            {
              value: "over_800",
              label: "More than 800 transactions",
              sets: CLEARS_TXN_RANGE,
              routesToFallback: true,
              fallback: HIGH_VOLUME_FALLBACK,
            },
          ],
        },
        txnRangeField("up_to_150", [
          ["up_to_30", "Up to 30", 30],
          ["31_60", "31–60", 60],
          ["61_90", "61–90", 90],
          ["91_120", "91–120", 120],
          ["121_150", "121–150", 150],
        ]),
        txnRangeField("151_300", [
          ["151_200", "151–200", 200],
          ["201_250", "201–250", 250],
          ["251_300", "251–300", 300],
        ]),
        txnRangeField("301_500", [
          ["301_350", "301–350", 350],
          ["351_400", "351–400", 400],
          ["401_500", "401–500", 500],
        ]),
        txnRangeField("501_800", [
          ["501_600", "501–600", 600],
          ["601_700", "601–700", 700],
          ["701_800", "701–800", 800],
        ]),
      ],
    },
    {
      key: "bookkeeping-books",
      title: "How your books work",
      subtitle: "Each of these adds a bit of monthly work, so each one is priced separately.",
      fields: [
        {
          type: "yesno",
          path: "vat_registered",
          label: "Is the business VAT-registered?",
          help: "This is about your business — T-TITE itself is not VAT-registered, so no VAT is ever added to your fee.",
          tooltip:
            "If you're registered for VAT with SARS you have to submit VAT201 returns, which means extra monthly reconciliation work.",
          required: true,
          reviewLabel: "Business is VAT-registered",
        },
        {
          type: "yesno",
          path: "banks_3plus",
          label: "Do you have 3 or more bank accounts to reconcile?",
          required: true,
          reviewLabel: "Three or more bank accounts to reconcile",
        },
        {
          type: "yesno",
          path: "inventory_tracking",
          label: "Do you hold inventory or stock that needs perpetual tracking?",
          tooltip:
            "Perpetual tracking means stock levels and stock values are updated continuously as things are bought and sold, rather than counted once a year.",
          required: true,
          reviewLabel: "Inventory requiring perpetual tracking",
        },
        {
          type: "yesno",
          path: "income_3plus",
          label: "Do you track 3 or more income streams or departments separately?",
          required: true,
          reviewLabel: "Three or more income streams / departments tracked separately",
        },
        {
          type: "yesno",
          path: "foreign_currency",
          label: "Do you have foreign currency transactions?",
          tooltip: "Money paid or received in a currency other than Rand.",
          required: true,
          reviewLabel: "Foreign currency transactions",
        },
      ],
    },
    {
      key: "bookkeeping-payroll",
      title: "Payroll and your accounting system",
      subtitle:
        "Two more things that affect your monthly fee — take them or leave them.",
      fields: [
        {
          type: "yesno",
          path: "payroll",
          label: "Would you like us to run your payroll as well?",
          help: "R40.43 per employee a month. That covers payslips and the monthly payroll submissions to SARS.",
          tooltip:
            "Payroll means working out what each person is paid and what gets deducted, issuing payslips, and filing the monthly returns that go with it.",
          yesHint: "Yes — please handle our payroll",
          noHint: "No — we'll keep doing our own",
          required: true,
          reviewLabel: "T-TITE to run the payroll",
        },
        {
          type: "number",
          path: "payroll_employees",
          label: "How many people are on the payroll?",
          help: "Include everyone who gets a payslip — permanent, part-time and directors drawing a salary.",
          required: true,
          requiredMessage: "Please tell us how many people are on the payroll.",
          min: 1,
          minMessage: "There has to be at least one person on the payroll.",
          max: 500,
          maxMessage:
            "That's more than our standard payroll pricing covers — please contact us and we'll scope it with you.",
          reviewLabel: "People on the payroll",
          showIf: (answers) => isYes(getPath(answers, "payroll")),
        },
        {
          type: "choice",
          path: "accounting_system",
          label: "Do you already have an accounting system?",
          help: "If you keep your own system we'll work inside it at no extra cost. Ours is R230 a month.",
          tooltip:
            "An accounting system is the software your books live in — for example Xero, Sage, QuickBooks or Zoho.",
          required: true,
          reviewLabel: "Accounting system",
          options: [
            {
              value: "own_keep",
              label: "Yes — and we'd like to carry on using it",
              hint: "We work inside your system. No system fee.",
            },
            {
              value: "own_switch",
              label: "Yes — but we'd rather move onto T-TITE's system",
              hint: "We migrate you across and keep it running for you. R230 a month.",
            },
            {
              value: "none",
              label: "No — we don't have one",
              hint: "We set you up on T-TITE's system and keep it running. R230 a month.",
            },
          ],
        },
      ],
    },
    {
      key: "bookkeeping-setup",
      title: "Getting you set up",
      subtitle:
        "One once-off item, charged on your first invoice and separate from your monthly fee. Only one of these can apply.",
      fields: [
        {
          type: "choice",
          path: "books_origin",
          label: "Which of these best describes where your books are coming from?",
          help: "Pick the one that fits. If your books are already set up and up to date, there's nothing extra to do.",
          tooltip:
            "A chart of accounts is the list of categories your transactions get sorted into. Opening balances are your starting figures carried in from before we take over.",
          required: true,
          reviewLabel: "Where your books are coming from",
          options: [
            {
              value: "new",
              label: "We've never had bookkeeping done before",
              hint: "We set your chart of accounts and opening balances up from scratch.",
            },
            {
              value: "migration",
              label: "We're moving from another bookkeeper or accounting system",
              hint: "We take over your existing books and migrate them across.",
            },
            {
              value: "none",
              label: "Neither — our books are already set up",
              hint: "Nothing to set up or migrate, so there's no once-off fee.",
            },
          ],
        },
      ],
    },
  ];
}

/* ------------------------------------------------------------------ */
/* Step assembly                                                       */
/* ------------------------------------------------------------------ */

const BUILDERS = {
  afs: afsSteps,
  assurance: assuranceSteps,
  bookkeeping: bookkeepingSteps,
};

/** Every step in the flow, including the review step and the contact gate. */
export function buildSteps(service, answers) {
  const builder = BUILDERS[service];
  if (!builder) return [];
  const questionSteps = builder(answers || {}).map((step) => ({ ...step, kind: "questions" }));
  return [
    ...questionSteps,
    {
      key: "review",
      kind: "review",
      title: "Check your answers",
      subtitle:
        "Everything you've told us, in one place. Change anything that isn't right — this is what your price is built from.",
      fields: [],
    },
    {
      key: "contact",
      kind: "contact",
      title: "Almost there",
      subtitle: "Pop in your details and we'll show you your price.",
      fields: [],
    },
  ];
}

/** Fields on a step that are currently relevant (e.g. transaction count is
 *  hidden for a dormant year). */
export function visibleFields(step, answers) {
  return (step.fields || []).filter((field) => !field.showIf || field.showIf(answers));
}

/** Human-readable value for the review summary and the admin view. */
export function formatFieldValue(field, answers) {
  const value = getPath(answers, field.path);
  if (value === null || value === undefined || value === "") return "\u2014";
  switch (field.type) {
    case "currency":
      return money(value);
    case "number":
      return formatCount(value);
    case "yesno":
      return isYes(value) ? "Yes" : "No";
    case "choice": {
      const option = (field.options || []).find((o) => String(o.value) === String(value));
      return option ? option.label : String(value);
    }
    default:
      return String(value);
  }
}

/** Inline validation for one field. Returns an error string or null. */
export function validateField(field, answers) {
  const value = getPath(answers, field.path);
  const blank = value === null || value === undefined || value === "";

  if (field.required && blank) {
    if (field.requiredMessage) return field.requiredMessage;
    if (field.type === "yesno") return "Please choose Yes or No.";
    if (field.type === "choice") return "Please choose one of the options.";
    return "Please enter a figure — use 0 if it doesn't apply.";
  }
  if (blank) return null;

  if (field.type === "currency" || field.type === "number") {
    const number = Number(value);
    if (Number.isNaN(number)) return "Please enter numbers only.";
    if (!field.allowNegative && number < 0) return "This can't be a negative amount.";
    if (field.min !== undefined && number < field.min) {
      return field.minMessage || `This can't be less than ${field.min}.`;
    }
    if (field.max !== undefined && number > field.max) {
      return field.maxMessage || `This can't be more than ${field.max}.`;
    }
    if (number > 1e12) return "That figure looks too large — please check it.";
  }
  return null;
}

export function validateStep(step, answers) {
  const errors = {};
  visibleFields(step, answers).forEach((field) => {
    const error = validateField(field, answers);
    if (error) errors[field.path] = error;
  });
  return errors;
}

/** Does the chosen option on this step route straight to the fallback? */
export function stepRoutesToFallback(step, answers) {
  return Boolean(routedFallbackOption(step, answers));
}

/** The chosen option that routes to the fallback, if there is one. */
function routedFallbackOption(step, answers) {
  for (const field of visibleFields(step, answers)) {
    if (field.type !== "choice") continue;
    const value = getPath(answers, field.path);
    const option = (field.options || []).find((o) => String(o.value) === String(value));
    if (option?.routesToFallback) return option;
  }
  return null;
}

/**
 * The words to use when a chosen option sends them to our team instead of a
 * price. Most options are happy with the general wording; some say why in their
 * own terms.
 */
export function routedFallbackCopy(step, answers) {
  return routedFallbackOption(step, answers)?.fallback || null;
}

/**
 * The turnover our standard bookkeeping pricing covers.
 *
 * Kept in step with `DEFAULT_RATES["bookkeeping"]["turnover_cap"]` on the
 * server, which remains the only place eligibility is actually decided. This
 * copy exists so the calculator can offer the finance function at the right
 * moment instead of waiting for the server to say no.
 */
export const BOOKKEEPING_TURNOVER_CAP = 20_000_000;

/**
 * Should we offer the outsourced finance function instead of a bookkeeping
 * price? Only on the first question, and only where they have told us the
 * revenue is above what the standard pricing is built for. It is a signpost,
 * never a refusal: they can carry on with bookkeeping from the fork.
 */
export function offersFinanceFunction(service, step, answers) {
  if (service !== "bookkeeping" || step?.key !== "bookkeeping-turnover") return false;
  return getPath(answers, "revenue_r20m_or_less") === "no";
}

/** Flat, plain-language summary rows for the review step, each one able to jump
 *  back to the exact step (and field) it came from. */
export function buildReviewGroups(service, answers, steps) {
  const groups = [];
  steps.forEach((step, stepIndex) => {
    if (step.kind !== "questions") return;
    const fields = visibleFields(step, answers);
    if (!fields.length) return;
    groups.push({
      key: step.key,
      title: step.title,
      chip: step.chip,
      stepIndex,
      items: fields.map((field) => ({
        path: field.path,
        label: field.reviewLabel || field.label,
        value: formatFieldValue(field, answers),
        stepIndex,
      })),
    });
  });
  return groups;
}
